// src/components/contacts/ContactWorkspaceDrawer.tsx
//
// The CRM Contact Workspace as a right-side SlideOver drawer (replaces the
// old full-page /phone/contact/:id view). Opened from the Contacts list Call
// button AND from the deep-link route — this component is the SINGLE source
// of truth for the workspace UI in both cases.
//
// Layout (Outbound-drawer parity, stacked):
//   Header (name · company · phone · close)  ← SlideOver chrome
//   ├─ Call section + Call Notes             ← ContactWorkspaceCallColumn
//   └─ Research sections (Business → … → Recent Activity) ← OutboundContactWorkspacePanel
//
// VOICE: the shared Twilio Device instance is owned by PhonePage and passed in
// as `voice`. This component NEVER calls useTwilioVoiceDevice — no second
// Device, no duplicated listeners.

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SlideOver from '../ui/SlideOver';
import ContactWorkspaceCallColumn, {
  useCallTerminalRefetch,
} from './ContactWorkspaceCallColumn';
import OutboundContactWorkspacePanel from './OutboundContactWorkspacePanel';
import { PaymentMethodsPanel } from '../payments/PaymentMethodsPanel';
import { useGetOutboundContactWorkspaceQuery } from '../../api/outboundContactWorkspaceApi';
import { useAppDispatch } from '../../store/store';
import type { UseTwilioVoiceDeviceResult } from '../../hooks/useTwilioVoiceDevice';

interface Props {
  open: boolean;
  /** CRM contact id whose workspace to show; null when the drawer is closed. */
  crmContactId: string | null;
  /** The SHARED voice-device instance, owned by PhonePage. */
  voice: UseTwilioVoiceDeviceResult;
  onClose: () => void;
}

export default function ContactWorkspaceDrawer({
  open,
  crmContactId,
  voice,
  onClose,
}: Props) {
  const contactId = (crmContactId ?? '').trim();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    // IMPORTANT: use `currentData`, NOT `data`. RTK Query's `data` keeps the
    // PREVIOUS arg's result while a new arg is fetching, which made the drawer
    // flash the previously-opened contact's data. `currentData` is scoped to
    // the CURRENT contactId (undefined while its fetch is in flight), so we
    // show the loading skeleton instead of stale data.
    currentData: workspaceData,
    error: workspaceError,
  } = useGetOutboundContactWorkspaceQuery(contactId, {
    skip: !open || !contactId,
  });

  // Terminal refetch is owned HERE (stable across the whole drawer lifetime),
  // watching the shared activeCall — fires once per terminal transition. The
  // hook no-ops when contactId is empty (drawer closed).
  useCallTerminalRefetch(voice.activeCall.kind, contactId, dispatch);

  const workspaceStatus: 'loading' | 'ok' | 'not_synced' | 'unavailable' =
    useMemo(() => {
      if (!contactId) return 'loading';
      if (workspaceError && !workspaceData) {
        const e = workspaceError as { status?: number; data?: { error?: string } };
        if (e.status === 404 && e.data?.error === 'CONTACT_NOT_SYNCED') {
          return 'not_synced';
        }
        return 'unavailable';
      }
      // No current-contact data yet → its fetch is in flight (or skipped).
      if (!workspaceData) return 'loading';
      return 'ok';
    }, [contactId, workspaceError, workspaceData]);

  const contact = workspaceData?.contact.data;
  const name = contact?.fullName ?? 'Contact';
  const headerBits = [contact?.company, contact?.phone].filter(Boolean).join(' · ');

  return (
    <SlideOver
      isOpen={open}
      onClose={onClose}
      title={name}
      description={headerBits || undefined}
      size="xl"
      // Sit BELOW the SuiteShell header instead of covering it. Uses the
      // shell's own CSS vars (falls back to 64px header + 16px gutter).
      topOffset="calc(var(--header-height, 64px) + var(--shell-gutter, 16px))"
    >
      {/* key={contactId} → switching contacts remounts the body fresh, so the
          call column (notes) AND the research panel (which reads its own RTK
          `data`) never carry over the previously-opened contact's state. */}
      <div
        key={contactId || 'none'}
        data-testid="contact-workspace-drawer"
        className="space-y-4"
      >
        {/* Phase D — schedule a Zoom-backed Event with THIS contact, reusing the
            ONE canonical Schedule create (prefilled + contact pre-associated).
            Creating the meeting does NOT send anything to the contact. */}
        {contactId ? (
          <button
            type="button"
            onClick={() =>
              navigate(
                `/schedule?new=1&kind=meeting&zoom=1&contactId=${encodeURIComponent(contactId)}`
              )
            }
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors"
          >
            Schedule Zoom meeting
          </button>
        ) : null}

        <ContactWorkspaceCallColumn
          crmContactId={contactId || null}
          workspaceData={workspaceData}
          workspaceStatus={workspaceStatus}
          voice={voice}
        />
        {/* Saved payment methods for THIS contact — managing one person's
            cards, not a global payments surface. Sits below the call column
            (the contact's primary detail) and above research/activity, which
            is lower-priority.

            It takes the drawer's own `contactId`: there is no second contact
            lookup, and switching contacts remounts it with everything else via
            the `key` above.

            The panel is rendered, never reimplemented. It owns its heading,
            its loading/error/empty states, and the add flow. It offers no
            collection action — taking payment is a different authority and is
            not part of this section. */}
        {contactId ? <PaymentMethodsPanel contactId={contactId} /> : null}

        {contactId ? (
          <OutboundContactWorkspacePanel crmContactId={contactId} />
        ) : null}
      </div>
    </SlideOver>
  );
}
